import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Button,
  Container, 
  Typography, 
  Grid,
  useTheme,
  useMediaQuery,
  Stack,
  Paper
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { motion } from 'framer-motion';

// Component for an animated gradient button
const GlassButton = ({ children, icon, ...props }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <motion.div
      whileHover={{ 
        scale: 1.05,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.95 }}
      style={{ display: 'inline-block', width: 'auto' }}
    >
      <Button
        {...props}
        sx={{
          py: 2,
          px: 3.5,
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          color: 'primary.main',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontSize: '1.1rem',
          width: 'auto',
          ...props.sx
        }}
      >
        {icon && <Box component="span" sx={{ opacity: 0.85 }}>{icon}</Box>}
        {children}
      </Button>
    </motion.div>
  );
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  return (
    <Box>
      {/* Hero Section with Animated Background and Blob */}
      <Box 
        sx={{ 
          minHeight: { xs: '100vh', md: '100vh' },
          position: 'relative',
          backgroundImage: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%), url(https://images.unsplash.com/photo-1528127269322-539801943592?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          overflow: 'hidden',
          pt: { xs: 8, sm: 8, md: 0 },
          pb: { xs: 8, sm: 8, md: 6 },
        }}
      >
        {/* Animated blob decorations */}
        <Box 
          component={motion.div}
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4],
            rotate: [0, 10, 0],
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          sx={{
            position: 'absolute',
            width: { xs: '400px', sm: '600px' },
            height: { xs: '400px', sm: '600px' },
            top: { xs: '-20%', sm: '-10%' },
            right: '-10%',
            background: 'radial-gradient(circle, rgba(94, 23, 235, 0.3) 0%, rgba(94, 23, 235, 0) 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        
        <Box 
          component={motion.div}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            rotate: [0, -10, 0],
          }}
          transition={{ 
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1 
          }}
          sx={{
            position: 'absolute',
            width: '500px',
            height: '500px',
            bottom: '-20%',
            left: '-5%',
            background: 'radial-gradient(circle, rgba(34, 202, 172, 0.3) 0%, rgba(34, 202, 172, 0) 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, mt: { xs: 8, md: 0 } }}>
          <Grid container spacing={{ xs: 4, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7} lg={6} sx={{ px: { xs: 2, sm: 3 } }}>
              <Box sx={{ position: 'relative' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
              >
                <Typography 
                  component="h1" 
                  color="white" 
                    sx={{ 
                      fontSize: { xs: '2.5rem', sm: '2.8rem', md: '3.5rem', lg: '4rem' },
                      fontWeight: 800,
                      lineHeight: { xs: 1.2, sm: 1.1 },
                      mb: { xs: 3, sm: 3 },
                      background: 'linear-gradient(135deg, #ffffff 0%, #d1c4ff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      wordBreak: 'break-word',
                      hyphens: 'manual',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxWidth: '100%',
                    }}
                  >
                    Your Dream{' '}
                    <Box component="span" sx={{ 
                      display: { xs: 'inline', sm: 'inline' }
                    }}>
                      Vacation,
                    </Box>{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        color: '#22caac', 
                        WebkitTextFillColor: '#22caac',
                        display: { xs: 'block', sm: 'inline' }
                      }}
                    >
                      Reimagined
                    </Box>
                  </Typography>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Typography 
                    variant="h6" 
                    color="white"
                    sx={{ 
                      opacity: 0.9,
                      mb: { xs: 4, sm: 4 },
                      fontSize: { xs: '1.25rem', sm: '1.05rem', md: '1.15rem' },
                      fontWeight: 400,
                      lineHeight: { xs: 1.6, sm: 1.6 },
                      maxWidth: '600px',
                      wordSpacing: { xs: '0.05em', sm: 'normal' },
                      letterSpacing: { xs: '0.01em', sm: 'normal' },
                    }}
                  >
                    Create stunning AI-powered travel itineraries tailored to 
                    your preferences in seconds. Discover hidden gems and 
                    unforgettable experiences.
                  </Typography>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={{ xs: 3, sm: 3 }}
                    sx={{ 
                      mt: { xs: 4, sm: 4 },
                      alignItems: { xs: 'center', sm: 'flex-start' },
                      width: '100%'
                    }}
                  >
                    <GlassButton
                      component={Link}
                      to="/planner"
                      icon={<AutoAwesomeIcon />}
                      sx={{
                        width: { xs: 'auto', sm: 'auto' },
                        minWidth: { xs: '220px', sm: 'auto' },
                        maxWidth: { xs: '90%', sm: 'none' },
                        py: { xs: 2, sm: 2 },
                        px: { xs: 3, sm: 3.5 },
                        fontSize: { xs: '1.1rem', sm: '1.1rem' },
                      }}
                    >
                      Start Planning
                    </GlassButton>
                  </Stack>
                </motion.div>
              </Box>
            </Grid>
            
            {!isMobile && (
              <Grid item md={5} lg={6}>
                <Box 
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                  sx={{ 
                    position: 'relative',
                    mt: { md: 4, lg: 0 }
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1974&auto=format&fit=crop"
                    alt="Travel Planning"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '500px',
                      objectFit: 'cover',
                      borderRadius: 4,
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      border: '4px solid rgba(255, 255, 255, 0.1)',
                    }}
                  />
                  
                  {/* Floating badges */}
                  <Paper
                    component={motion.div}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1, duration: 0.6 }}
                    sx={{
                      position: 'absolute',
                      bottom: '20%',
                      left: '-10%',
                      py: 1.5,
                      px: 2,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <LocalCafeIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Personalized Suggestions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Based on your interests
                      </Typography>
                    </Box>
                  </Paper>
                  
                  <Paper
                    component={motion.div}
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1.3, duration: 0.6 }}
                    sx={{
                      position: 'absolute',
                      top: '10%',
                      right: '-5%',
                      py: 1.5,
                      px: 2,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <NightsStayIcon sx={{ color: theme.palette.secondary.main }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Magical Experiences
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Discover hidden gems
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 